// src/CONTAINERCONTROL/equipmentCostRuleCrud.jsx
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from '@mui/material'
import { BufferService, LoadingContext, M_Button, M_Fieldset, M_Header, M_MessageBoard, NavigateContext } from 'src/M_Components'
import { changes, fields } from 'src/components/utils/fields'
import { AttachMoney, Rule } from '@mui/icons-material'

export const EquipmentCostRuleCrud = () => {
    const [id, setId] = useState(sessionStorage.getItem('selectedTab'))
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)
    const ctx2 = useContext(NavigateContext)

    const [data, setData] = useState({})
    const [operation, setOperation] = useState('')
    const [leavingPage, setLeavingPage] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [editingId, setEditingId] = useState(null)
    const [isFirstTime, setIsFirstTime] = useState(true)

    const sessionKeys = ['operation', 'data', 'leavingPage']

    const costRuleService = new BufferService('EquipmentCostRule', 'secondary')

    const matches = useMediaQuery('(min-width:900px)')

    // ── Field definitions ────────────────────────────────────────────────────

    const RuleFields = [
        {
            display: t('Move'),
            required: true,
            code: 'movementTypeCode',
            type: 'foreignKey',
            entity: 'MovementType',
            api: 'secondary',
            filterField: 'Code',
            labelFields: ['Code'],
            fields: [
                { key: 'Code', value: 'movementTypeCode' },
                { key: 'Name', value: 'movementTypeName' },
                { key: 'Id', value: 'movementTypeId' }
            ]
        },
        {
            display: t('Eq. Type'),
            required: false,
            code: 'equipmentTypeCode',
            type: 'foreignKey',
            entity: 'EquipmentType',
            api: 'secondary',
            filterField: 'Code',
            labelFields: ['Code'],
            fields: [
                { key: 'Code', value: 'equipmentTypeCode' },
                { key: 'Name', value: 'equipmentTypeName' },
                { key: 'Id', value: 'equipmentTypeId' }
            ]
        },
        {
            display: t('From'),
            required: false,
            code: 'movementFromCode',
            type: 'foreignKey',
            entity: 'Location',
            api: 'main',
            filterField: 'LocationName',
            labelFields: ['LocationCode'],
            fields: [
                { key: 'LocationCode', value: 'movementFromCode' },
                { key: 'Id', value: 'movementFromId' }
            ]
        },
        {
            display: t('To'),
            required: false,
            code: 'movementToCode',
            type: 'foreignKey',
            entity: 'Location',
            api: 'main',
            filterField: 'LocationName',
            labelFields: ['LocationCode'],
            fields: [
                { key: 'LocationCode', value: 'movementToCode' },
                { key: 'Id', value: 'movementToId' }
            ]
        },
        {
            display: t('Tr. Means'),
            required: false,
            code: 'transportMeansCode',
            type: 'foreignKey',
            entity: 'TransportMeans',
            api: 'secondary',
            filterField: 'Code',
            labelFields: ['Code'],
            fields: [
                { key: 'Code', value: 'transportMeansCode' },
                { key: 'Id', value: 'transportMeansId' }
            ]
        },
    ]

    const ServiceFields = [
        // {
        //     display: t('Service Code'),
        //     required: true,
        //     code: 'serviceTypeCode',
        //     type: 'text',
        // },
        {
            display: t('ServiceCode'),
            size: 180,
            required: true,
            code: 'serviceTypeCode',
            type: 'foreignKey',
            entity: 'ServiceType',
            tableLabel: t('ServiceType'),
            filterField: 'code',
            labelFields: ['ServiceCode'],
            fields: [
                { key: 'ServiceCode', value: 'serviceTypeCode' },
                { key: 'ServiceName', value: 'serviceTypeName' },
                { key: 'Id', value: 'serviceTypeId' },
            ],
            favoritesFieldName: t('ServiceCode'),
            favoritesGroupName: 'EquipmentCost',
            favoritesVarKey: 'serviceTypeCode',
            favoritesIdKey: 'serviceTypeId',
            },
        {
            display: t('Service Name'),
            code: 'serviceTypeName',
            type: 'disabled',
        },
        {
            display: t('Company Code'),
            required: true,
            code: 'accountCode',
            type: 'foreignKey',
            entity: 'Account',
            api: 'main',
            filterField: 'AccountCode',
            labelFields: ['AccountCode'],
            fields: [
                { key: 'AccountCode', value: 'accountCode' },
                { key: 'Id', value: 'accountId' }
            ]
        },
        {
            display: t('Estimated Cost'),
            required: true,
            code: 'estimatedCost',
            type: 'decimal',
        },
        {
            display: t('Description'),
            code: 'description',
            type: 'textArea',
            fullWidth: true
        },
    ]

    // ── Session storage ──────────────────────────────────────────────────────

    const sessionStorageMachine = (id, key, value) => {
        // eslint-disable-next-line
        if (eval(value) instanceof Map) {
            // eslint-disable-next-line
            sessionStorage.setItem(`${id}${key}`, JSON.stringify(Array.from(eval(value))))
        } else {
            // eslint-disable-next-line
            sessionStorage.setItem(`${id}${key}`, JSON.stringify(eval(value)))
        }
    }

    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true)
        const tempO = JSON.parse(sessionStorage.getItem(`${sessionID}operation`))
        const tempD = JSON.parse(sessionStorage.getItem(`${sessionID}data`))
        const tempLP = JSON.parse(sessionStorage.getItem(`${sessionID}leavingPage`))

        if ((tempO && tempO !== '') || (tempD && Object.keys(tempD).length !== 0)) {
            setOperation(new String(tempO))
            setData(new Object(tempD))
            setLeavingPage(tempLP || false)
            ctx.setIsLoad(false)
            return
        }
        get()
    }

    const get = () => {
        ctx.setIsLoad(false)
    }

    // ── Lifecycle ────────────────────────────────────────────────────────────

    useEffect(() => {
        if (isFirstTime) {
            ctx.setIsLoad(true)
            getSessionStorageData(id)
            setIsFirstTime(false)
            sessionStorage.setItem(`tab${id}`, JSON.stringify(sessionKeys))

            const editingRule = JSON.parse(sessionStorage.getItem('editingEquipmentCostRule'))
            const operationType = sessionStorage.getItem('operationType')

            if (editingRule && (operationType === 'Edit' || operationType === 'Copy')) {
                loadRule(editingRule.Id, operationType === 'Copy')
                sessionStorage.removeItem('editingEquipmentCostRule')
                sessionStorage.removeItem('operationType')
            } else if (operationType === 'Create') {
                setOperation('Create')
                sessionStorage.removeItem('operationType')
            }
        }
    }, [])

    useEffect(() => {
        const newId = sessionStorage.getItem('selectedTab')
        if (newId !== id) {
            setId(newId)
            getSessionStorageData(newId)
        }
    })

    useEffect(() => {
        sessionKeys.forEach((key) => {
            sessionStorageMachine(id, key, key)
        })
        // eslint-disable-next-line
    }, [id, operation, data, leavingPage])

    // ── Load for edit/copy ───────────────────────────────────────────────────

    const loadRule = (ruleId, isCopy = false) => {
        ctx.setIsLoad(true)
        costRuleService.getId(ruleId).then(response => {
            ctx.setIsLoad(false)
            if (response?.name === 'AxiosError') {
                ctx.toastMessages('error', '', t('Failed to load Equipment Cost Rule'))
                return
            }

            const ruleData = {
                movementTypeId: response.MovementTypeId?.toString(),
                movementTypeCode: response.MovementTypeCode,
                movementTypeName: response.MovementTypeName,

                equipmentTypeId: response.EquipmentTypeId?.toString(),
                equipmentTypeCode: response.EquipmentTypeCode,
                equipmentTypeName: response.EquipmentTypeName,

                movementFromId: response.MovementFromId?.toString(),
                movementFromCode: response.MovementFromCode,

                movementToId: response.MovementToId?.toString(),
                movementToCode: response.MovementToCode,

                transportMeansId: response.TransportMeansId?.toString(),
                transportMeansCode: response.TransportMeansCode,

                serviceTypeId: response.ServiceTypeId?.toString(),
                serviceTypeCode: response.ServiceTypeCode,
                serviceTypeName: response.ServiceTypeName,

                accountId: response.AccountId?.toString(),
                accountCode: response.AccountCode,

                estimatedCost: response.EstimatedCost?.toString(),
                description: response.Description,
            }

            if (isCopy) {
                setIsEditMode(false)
                setEditingId(null)
                setOperation('Create')
            } else {
                setIsEditMode(true)
                setEditingId(ruleId)
                setOperation('Edit')
            }

            setData(ruleData)
        }).catch(() => {
            ctx.setIsLoad(false)
            ctx.toastMessages('error', '', t('Failed to load Equipment Cost Rule'))
        })
    }

    // ── Save ─────────────────────────────────────────────────────────────────

    const onSave = () => {
        if (!data.movementTypeId || !data.serviceTypeCode || !data.accountId || !data.estimatedCost) {
            ctx.toastMessages('error', '', t('Missing required fields'))
            return
        }

        const payload = {
            MovementTypeId: parseInt(data.movementTypeId),

            EquipmentTypeId: data.equipmentTypeId ? parseInt(data.equipmentTypeId) : null,

            MovementFromId: data.movementFromId || null,
            MovementFromCode: data.movementFromCode || null,

            MovementToId: data.movementToId || null,
            MovementToCode: data.movementToCode || null,

            TransportMeansId: data.transportMeansId ? parseInt(data.transportMeansId) : null,

            ServiceTypeId: '00000000-0000-0000-0000-000000000000', // placeholder until integration
            ServiceTypeCode: data.serviceTypeCode || null,
            ServiceTypeName: data.serviceTypeName || null,

            AccountId: data.accountId,
            AccountCode: data.accountCode,

            EstimatedCost: parseFloat(data.estimatedCost),
            Description: data.description || null,
        }

        ctx.setIsLoad(true)

        const savePromise = isEditMode && editingId
            ? costRuleService.update(editingId, payload)
            : costRuleService.post(payload)

        savePromise.then(response => {
            ctx.setIsLoad(false)
            if (response?.name === 'AxiosError') {
                ctx.toastMessages('error', response.message, t('Error'))
                return
            }
            const successMessage = isEditMode
                ? `${t('Equipment Cost Rule')} ${t('updatedSuccessfully')}`
                : `${t('Equipment Cost Rule')} ${t('savedSuccessfully')}`
            ctx.toastMessages('success', '', successMessage)
            ctx2.closeTab(JSON.parse(id))
        }).catch(() => {
            ctx.setIsLoad(false)
            ctx.toastMessages('error', '', t('Error saving Equipment Cost Rule'))
        })
    }

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div>
            <M_Header
                element={
                    <>
                        <M_Button
                            tooltip={t('Back')}
                            label={t('Back')}
                            icon={<i className="pi pi-arrow-left iconTable" style={{ width: '20px', color: ctx.secondaryColor ? ctx.secondaryColor : 'var(--color-black)' }} />}
                            onClick={() => setLeavingPage(true)}
                        />
                        <M_Button
                            tooltip={t('Save')}
                            label={t('Save')}
                            icon={<i className="pi pi-save" />}
                            onClick={onSave}
                        />
                    </>
                }
            />

            <div style={{ margin: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: matches ? 'auto auto' : 'auto' }}>

                    {/* Rule conditions */}
                    <M_Fieldset
                        labelInside
                        label={
                            <div style={{ display: 'flex', alignItems: 'center', fontSize: '25px', marginLeft: '10px' }}>
                                <Rule style={{ marginRight: '10px', scale: '1.3' }} />
                                {t('Rule Conditions')}
                            </div>
                        }
                        elements={
                            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', justifyContent: 'space-evenly' }}>
                                {RuleFields.map((field, index) =>
                                    fields(field, index, data, setData, null, null, '250px')
                                )}
                            </div>
                        }
                    />

                    {/* Service & Cost */}
                    <M_Fieldset
                        labelInside
                        label={
                            <div style={{ display: 'flex', alignItems: 'center', fontSize: '25px', marginLeft: '10px' }}>
                                <AttachMoney style={{ marginRight: '10px', scale: '1.3' }} />
                                {t('Service & Cost')}
                            </div>
                        }
                        elements={
                            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', justifyContent: 'space-evenly' }}>
                                {ServiceFields.map((field, index) => {
                                    if (field.fullWidth) {
                                        return (
                                            <div key={index} style={{ gridColumn: '1 / -1', width: '100%', marginTop: '10px' }}>
                                                {fields(field, index, data, setData, false, null, '100%')}
                                            </div>
                                        )
                                    }
                                    return fields(field, index, data, setData, null, null, '250px')
                                })}
                            </div>
                        }
                    />
                </div>
            </div>

            <M_MessageBoard
                warning
                open={leavingPage}
                title={t('leavingPageTitle')}
                body={t('leavingPageBody')}
                footer={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        <M_Button hoverEffect normal labelColor={'white'} backgroundColor={'var(--color-red)'} onClick={() => setLeavingPage(false)} label={t('ButtonCancel')} />
                        <M_Button hoverEffect normal labelColor={'white'} backgroundColor={'var(--color-green)'} onClick={() => ctx2.closeTab(JSON.parse(id))} label={t('ButtonConfirm')} />
                    </span>
                }
            />
        </div>
    )
}